/**
 * Cliente HTTP unificado (Axios) para o Corp.
 * Resposta padrão: { data, meta }. get/post/put/patch/delete retornam sempre esse envelope.
 * O client deve ser usado apenas após a inicialização do app (createApp().use(pinia)).
 *
 * Ver docs/arquitetura-api-unificada.md e docs/README-TDD-BASE.md.
 */

import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import {
  runCorpAuthenticatedErrorPolicy,
  SKIP_TERMS_REDIRECT_KEY,
} from '@/api/corpApiResponseErrorPolicy'
import { useAuthStore } from '@/stores/auth'
import { handleError } from '@/types/apiErrors'

export { SKIP_TERMS_REDIRECT_KEY }

const BASE_URL = import.meta.env.VITE_API_URL || 'https://api-corp.ecx.local'
const TIMEOUT_MS = 5 * 60 * 1000
const IS_TEST = typeof import.meta !== 'undefined' && (import.meta as { env?: { MODE?: string } }).env?.MODE === 'test'
export interface ApiResponseEnvelope {
  data: unknown
  meta: unknown
}

export type GetParams = Record<string, string | string[]>

function createBaseConfig(): Record<string, unknown> {
  const config: Record<string, unknown> = {
    baseURL: BASE_URL,
    timeout: TIMEOUT_MS,
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
  }
  if (IS_TEST) {
    config.adapter = (cfg: unknown) =>
      Promise.resolve({
        data: {},
        status: 200,
        statusText: 'OK',
        headers: {},
        config: cfg,
      })
  }
  return config
}

function attachAuthHeaders(config: InternalAxiosRequestConfig, includeAccountId: boolean): void {
  const store = useAuthStore()
  const headers = store.getAuthHeaders()
  if (!config.headers) return
  Object.assign(config.headers, headers)
  if (!includeAccountId && config.headers) {
    delete (config.headers as Record<string, unknown>)['X-ACCOUNT-ID']
  }
}

function createRequestInterceptor(includeAccountId: boolean) {
  return (config: InternalAxiosRequestConfig) => {
    attachAuthHeaders(config, includeAccountId)
    // FormData: deixa o axios/browser definir Content-Type com boundary (não usar application/json)
    if (config.data instanceof FormData && config.headers) {
      const headers = config.headers as Record<string, unknown>
      delete headers['Content-Type']
      delete headers['content-type']
    }
    return config
  }
}

function toEnvelope(axiosData: unknown): ApiResponseEnvelope {
  const body = axiosData as { data?: unknown; meta?: unknown } | null
  return {
    data: body?.data ?? body ?? {},
    meta: body?.meta ?? {},
  }
}

function createResponseErrorHandler(instance: AxiosInstance) {
  return async (error: AxiosError): Promise<never> => {
    const outcome = await runCorpAuthenticatedErrorPolicy(instance, error)
    if (outcome.kind === 'retry') {
      return outcome.promise as Promise<never>
    }
    if (error.response) {
      handleError(error.response)
    }
    handleError(undefined)
  }
}

/** Handler de erro para rotas públicas: não faz refresh/redirect em 401, apenas propaga o erro. */
function createPublicResponseErrorHandler() {
  return (error: AxiosError): never => {
    if (error.response) {
      handleError(error.response)
    }
    handleError(undefined)
  }
}

/** Config para GET: params de query; skipTermsRedirect para não redirecionar em 403 terms_not_accepted. */
export interface GetConfig {
  params?: GetParams
  skipTermsRedirect?: boolean
  /** Headers extras (ex.: mock de desenvolvimento). */
  headers?: Record<string, string>
}

export interface CorpApiClient {
  get(path: string, config?: GetConfig): Promise<ApiResponseEnvelope>
  getWithPagination(path: string, params?: GetParams, config?: Pick<GetConfig, 'headers' | 'skipTermsRedirect'>): Promise<ApiResponseEnvelope>
  post(path: string, body?: unknown, config?: Record<string, unknown>): Promise<ApiResponseEnvelope>
  put(path: string, body?: unknown, config?: Record<string, unknown>): Promise<ApiResponseEnvelope>
  patch(path: string, body?: unknown, config?: Record<string, unknown>): Promise<ApiResponseEnvelope>
  delete(path: string, config?: Record<string, unknown>): Promise<ApiResponseEnvelope>
  defaults?: { baseURL?: string; timeout?: number; headers?: Record<string, unknown> }
  baseURL?: string
  timeout?: number
  headers?: Record<string, unknown>
  interceptors?: AxiosInstance['interceptors']
  request: AxiosInstance['request']
}

export function createCorpApiClient(includeAccountId: boolean): CorpApiClient {
  const instance = axios.create(createBaseConfig() as Parameters<typeof axios.create>[0])
  instance.interceptors.request.use(createRequestInterceptor(includeAccountId))
  const interceptRequestError = createResponseErrorHandler(instance)
  instance.interceptors.response.use(
    (res) => res,
    (err) => interceptRequestError(err)
  )

  async function wrapRequest(
    clientRef: CorpApiClient,
    config: Parameters<AxiosInstance['request']>[0]
  ): Promise<ApiResponseEnvelope> {
    const request = await clientRef.request(config)
    return toEnvelope(request.data)
  }

  const client: CorpApiClient = {
    defaults: instance.defaults,
    baseURL: instance.defaults?.baseURL,
    timeout: instance.defaults?.timeout,
    headers: instance.defaults?.headers as Record<string, unknown> | undefined,
    interceptors: instance.interceptors,
    request: instance.request.bind(instance),
    get: (path: string, config?: GetConfig) =>
      wrapRequest(
        client,
        {
          ...config,
          method: 'GET',
          url: path,
          params: config?.params,
          headers: config?.headers,
          [SKIP_TERMS_REDIRECT_KEY]: config?.skipTermsRedirect,
        } as Parameters<AxiosInstance['request']>[0]
      ),
    getWithPagination: (path, params, config) =>
      wrapRequest(client, {
        method: 'GET',
        url: path,
        params,
        headers: config?.headers,
        [SKIP_TERMS_REDIRECT_KEY]: config?.skipTermsRedirect,
      } as Parameters<AxiosInstance['request']>[0]),
    post: (path: string, body?: unknown, config?: Record<string, unknown>) =>
      wrapRequest(client, { ...config, method: 'POST', url: path, data: body }),
    put: (path: string, body?: unknown, config?: Record<string, unknown>) =>
      wrapRequest(client, { ...config, method: 'PUT', url: path, data: body }),
    patch: (path: string, body?: unknown, config?: Record<string, unknown>) =>
      wrapRequest(client, { ...config, method: 'PATCH', url: path, data: body }),
    delete: (path: string, config?: Record<string, unknown>) =>
      wrapRequest(client, { ...config, method: 'DELETE', url: path }),
  }

  return client
}

/** Request interceptor para cliente público: FormData sem Content-Type (boundary definido pelo axios). */
function publicRequestInterceptor(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  if (config.data instanceof FormData && config.headers) {
    const headers = config.headers as Record<string, unknown>
    delete headers['Content-Type']
    delete headers['content-type']
  }
  return config
}

/**
 * Cliente para endpoints públicos (sem autenticação).
 * Não envia Authorization nem X-ACCOUNT-ID; em 401 não redireciona para login.
 */
export function createPublicCorpApiClient(): CorpApiClient {
  const instance = axios.create(createBaseConfig() as Parameters<typeof axios.create>[0])
  instance.interceptors.request.use(publicRequestInterceptor)
  const publicErrorHandler = createPublicResponseErrorHandler()
  instance.interceptors.response.use(
    (res) => res,
    (err) => {
      publicErrorHandler(err)
    }
  )

  async function wrapRequest(
    clientRef: CorpApiClient,
    config: Parameters<AxiosInstance['request']>[0]
  ): Promise<ApiResponseEnvelope> {
    const request = await clientRef.request(config)
    return toEnvelope(request.data)
  }

  const client: CorpApiClient = {
    defaults: instance.defaults,
    baseURL: instance.defaults?.baseURL,
    timeout: instance.defaults?.timeout,
    headers: instance.defaults?.headers as Record<string, unknown> | undefined,
    interceptors: instance.interceptors,
    request: instance.request.bind(instance),
    get: (path: string, config?: GetConfig) =>
      wrapRequest(client, { ...config, method: 'GET', url: path, params: config?.params }),
    getWithPagination: (path, params, config) =>
      wrapRequest(client, {
        method: 'GET',
        url: path,
        params,
        headers: config?.headers,
      } as Parameters<AxiosInstance['request']>[0]),
    post: (path: string, body?: unknown, config?: Record<string, unknown>) =>
      wrapRequest(client, { ...config, method: 'POST', url: path, data: body }),
    put: (path: string, body?: unknown, config?: Record<string, unknown>) =>
      wrapRequest(client, { ...config, method: 'PUT', url: path, data: body }),
    patch: (path: string, body?: unknown, config?: Record<string, unknown>) =>
      wrapRequest(client, { ...config, method: 'PATCH', url: path, data: body }),
    delete: (path: string, config?: Record<string, unknown>) =>
      wrapRequest(client, { ...config, method: 'DELETE', url: path }),
  }

  return client
}

export const apiClient = createCorpApiClient(true)
export const apiClientWithoutAccount = createCorpApiClient(false)
export const publicApiClient = createPublicCorpApiClient()

